import React, { useState, useEffect } from "react";
import "./userList.css";
import { Button, Table, Input, Image, Select, Modal, Tag } from "antd";
import { callApi } from "../../api/apiCaller";
import routes from "../../api/routes";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const getAllUser = async () => {
    const url = routes.getAllUsers;

    await callApi(
      "GET",
      url,
      null,
      setIsLoading,
      (res) => {
        const filteredUsers = (res?.users || []).filter(
          (u) => u.role !== "trainer" && u.role !== "admin"
        );
        setUsers(filteredUsers);
      },
      (err) => RedNotify(err?.message || "Failed to fetch users.")
    );
  };

  useEffect(() => {
    getAllUser();
  }, []);

  const handleDelete = async (index, event, userId) => {
    event.stopPropagation();
    Modal.confirm({
      title: "Are you sure you want to delete this user?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await callApi(
            "DELETE",
            `${routes.deleteUser}/${userId}`,
            {},
            () => {},
            (res) => {
              GreenNotify(res?.message || "User deleted successfully.");
              const updatedUsers = users.filter((user) => user._id !== userId);
              setUsers(updatedUsers);
            },
            (err) => RedNotify(err?.message || "Failed to delete user.")
          );
        } catch (error) {
          RedNotify("Unexpected error occurred during deletion.");
        }
      },
    });
  };

  const blocked = async (index, event, userId, blockStatus) => {
    const updatedUsers = [...users];
    const userIndex = updatedUsers.findIndex((user) => user._id === userId);

    if (userIndex !== -1) updatedUsers[userIndex].isBlocked = blockStatus;

    try {
      await callApi(
        "PATCH",
        `${routes.blockUser}/${userId}`,
        { isBlocked: blockStatus },
        () => {},
        () => {
          GreenNotify(
            `User ${blockStatus ? "blocked" : "unblocked"} successfully!`
          );
          setUsers(updatedUsers);
        },
        () => getAllUser()
      );
    } catch {
      RedNotify("Unexpected error occurred while changing block status.");
    }
  };

  const getRowClassName = (record) => {
    if (record.isBlocked) return "row-blocked";
    return "";
  };

  // 🔥 All Profile fields included here
  const columns = [
    { title: "Name", dataIndex: "name", align: "center" },
    { title: "Email", dataIndex: "email", align: "center" },
    {
      title: "Profile Picture",
      dataIndex: "profilePicture",
      align: "center",
      render: (src) =>
        src ? (
          <Image
            width={50}
            height={50}
            src={src}
            alt="profile"
            style={{ borderRadius: "50%" }}
          />
        ) : (
          "-"
        ),
    },
    { title: "Age", dataIndex: "age", align: "center" },
    { title: "Gender", dataIndex: "gender", align: "center" },
    {
      title: "Weight",
      dataIndex: "weight",
      align: "center",
      render: (w) => (w?.value ? `${w.value} ${w.unit}` : "-"),
    },
    {
      title: "Height",
      dataIndex: "height",
      align: "center",
      render: (h) => (h?.value ? `${h.value} ${h.unit}` : "-"),
    },

    {
      title: "Block User",
      dataIndex: "blockORUnblock",
      align: "center",
      render: (_, record, index) => (
        <Button
          style={{
            color: "white",
            backgroundColor: record.isBlocked ? "#52c41a" : "#f5222d",
          }}
          onClick={(e) => blocked(index, e, record.key, !record.isBlocked)}
        >
          {record.isBlocked ? "Unblock" : "Block"}
        </Button>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      align: "center",
      render: (_, record, index) => {
        return (
          <Button
            style={{ backgroundColor: "#f5222d", color: "white" }}
            onClick={(e) => handleDelete(index, e, record.key)}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  // Map all profile fields
  const data = users
    .map((item) => {
      const profile = item.profile || {};
      return {
        key: item._id,
        name: profile.name || "",
        email: item.email,
        role: item.role,
        profilePicture: profile.profilePicture || "",
        age: profile.age,
        gender: profile.gender,
        weight: profile.weight,
        height: profile.height,
        expertise: profile.expertise,
        otherExpertise: profile.otherExpertise,
        certificate: profile.certificate,
        taxationForms: profile.taxationForms,
        allowChats: profile.allowChats,
        bankDetails: profile.bankDetails,
        isBlocked: item.isBlocked,
        isDeleted: item.isDeleted,
      };
    })
    .filter((item) => {
      const nameMatch = item.name
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      const emailMatch = item.email
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      return nameMatch || emailMatch;
    });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchText]);

  return (
    <div className="admin-products-main-container">
      <div className="configure-server-roles-main-heading-container">
        <h1 style={{ marginInline: "24px", marginBlock: "12px" }}>
          Users List
        </h1>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginInline: "24px",
            marginBlock: "12px",
          }}
        >
          <Input
            placeholder="Search Name or Email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "30rem" }}
          />
        </div>
      </div>

      <div className="server-roles-tb-main-container">
        <Table
          rowClassName={getRowClassName}
          columns={columns}
          dataSource={data}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data.length,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
          className="subscriptionapi-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Loader loading={isloading} />
    </div>
  );
};

export default UserList;
